/**
 * FairwayBid – Zielgruppensteuerung
 *
 * Sharetribe Hosted (No-Code) kann Navigation und Startseite nicht danach
 * unterscheiden, wer gerade davorsitzt. Dieses Modul holt das nach:
 * Es bestimmt die "Zielgruppe" des aktuellen Besuchers und stellt zwei
 * Filter bereit – einen für die Links in der Topbar, einen für die
 * Abschnitte der Startseite.
 *
 * Zielgruppen:
 *   'anonymous' – nicht angemeldet
 *   'customer'  – angemeldet als Golfclub / Golfanlage
 *   'provider'  – angemeldet als Händler oder Hersteller
 */

export const ANONYMOUS = 'anonymous';
export const CLUB = 'customer';
export const DEALER = 'provider';

// Alle Zielgruppen – für Inhalte, die immer sichtbar sein sollen.
export const ALL = [ANONYMOUS, CLUB, DEALER];

/**
 * Welche Topbar-Links sieht wer?
 *
 * Schlüssel ist die "Internal link address" aus der Console
 * (Content -> Top bar -> Custom links). Steht ein Link hier nicht drin,
 * ist er für alle sichtbar.
 *
 * Beispiel: '/p/so-funktioniert-es': [ANONYMOUS]
 *   -> nur nicht angemeldete Besucher sehen den Link.
 */
export const NAV_AUDIENCES = {
  // Werbeseiten: Sie fuehren zur Registrierung und richten sich an eine
  // bestimmte Seite. Wer schon angemeldet ist, hat sie hinter sich.
  '/p/so-funktioniert-es': [ANONYMOUS],
  '/p/fuer-golfanlagen': [ANONYMOUS],
  '/p/fuer-haendler': [ANONYMOUS],

  // "Geprüfte Anbieter" (/p/anbieter) steht in keiner Navigation mehr.
  // Grund: In der Kopfzeile standen "Anbieter" und "Für Händler"
  // nebeneinander und klangen nach derselben Seite, obwohl sie zwei
  // verschiedene Aufgaben haben - die eine wirbt um Händler, die andere
  // belegt gegenüber Golfanlagen, dass Anbieter geprüft werden.
  // Die Seite ist weiter erreichbar: Der Vertrauenssatz unter den beiden
  // Karten verlinkt sie genau dort, wo die Behauptung aufgestellt wird.
  // Sobald das Verzeichnis gefüllt ist, gehört sie zurück in die Kopfzeile.
  '/p/anbieter': [],
  '/signup': [ANONYMOUS],
  '/login': [ANONYMOUS],

  // Arbeitslinks: erst nach der Anmeldung sinnvoll, und je Rolle verschieden.
  '/s': [DEALER], // Offene Anfragen durchsuchen
  '/listings': [CLUB], // Eigene Anfragen verwalten
  '/faq': [CLUB, DEALER], // Haeufige Fragen - je Seite ein eigener Katalog
};

// Rechtliches und Unternehmensseiten bleiben fuer alle sichtbar und stehen
// deshalb bewusst nicht in NAV_AUDIENCES: /terms-of-service, /privacy-policy,
// /p/imprint, /p/about.

/**
 * Welche Abschnitte der Startseite sieht wer?
 *
 * Gesteuert über die "Anchor link ID" des Abschnitts in der Console
 * (Content -> Pages -> landing-page -> Abschnitt -> Anchor link ID).
 * Präfix entscheidet:
 *
 *   public-*  -> nur nicht angemeldete Besucher
 *   club-*    -> nur angemeldete Golfanlagen
 *   dealer-*  -> nur angemeldete Händler
 *   member-*  -> alle Angemeldeten (Golfanlagen und Händler)
 *   alles andere / keine ID -> alle
 */
const SECTION_PREFIXES = [
  { prefix: 'public-', audiences: [ANONYMOUS] },
  { prefix: 'club-', audiences: [CLUB] },
  { prefix: 'dealer-', audiences: [DEALER] },
  { prefix: 'member-', audiences: [CLUB, DEALER] },
];

/**
 * Bestimmt die Zielgruppe des aktuellen Besuchers.
 *
 * @param {Object} currentUser CurrentUser-Entity aus dem Redux-Store (oder null)
 * @returns {string} 'anonymous' | 'customer' | 'provider'
 */
export const getAudience = currentUser => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (!currentUser?.id) {
    return ANONYMOUS;
  }
  // Unbekannter oder fehlender Nutzertyp: wie ein Mitglied ohne Rolle behandeln.
  return userType === CLUB || userType === DEALER ? userType : CLUB;
};

/**
 * Filtert die Topbar-Links auf das, was diese Zielgruppe sehen soll.
 *
 * @param {Array} customLinks Links aus der Console
 * @param {string} audience Rückgabewert von getAudience
 * @returns {Array} gefilterte Links
 */
export const filterNavLinks = (customLinks, audience) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.filter(link => {
    const allowed = NAV_AUDIENCES[(link?.href)];
    return allowed ? allowed.includes(audience) : true;
  });
};

/**
 * Filtert die Abschnitte einer Seite auf das, was diese Zielgruppe sehen soll.
 *
 * @param {Array} sections Abschnitte aus dem Page-Asset
 * @param {string} audience Rückgabewert von getAudience
 * @returns {Array} gefilterte Abschnitte
 */
/**
 * Abschnitte, die in der Console noch stehen, aber nicht mehr gezeigt werden.
 *
 * 'public-registrieren' ("Jetzt starten") wiederholte die beiden Zielgruppen,
 * die beiden Knöpfe nach /signup und das Prüfversprechen – alles steht schon
 * in den Karten unter "Warum FairwayBid?". Die drei Aussagen, die nur dort
 * standen, sind vorher in die Karten gewandert (siehe benefits.js).
 *
 * Das gehört eigentlich in die Console: Content → Landing page → Abschnitt
 * "Jetzt starten" → Delete section. Solange er dort steht, hält ihn diese
 * Liste von der Seite fern. Wer ihn zurückholen will, nimmt die ID hier
 * wieder heraus – der Inhalt ist unangetastet.
 */
export const HIDDEN_SECTION_IDS = ['public-registrieren'];

/**
 * Überschriften, die seit den Empfangskacheln doppelt sind.
 *
 * "Willkommen zurück" mit der Erklärung, wo die eigenen Anfragen stehen,
 * sagt genau das, was die Kacheln jetzt zeigen.
 *
 * WICHTIG: Das betrifft nur die ÜBERSCHRIFT, nicht den Abschnitt. Diese
 * Überschrift gehört nämlich zum Dashboard-Abschnitt selbst - wer den
 * Abschnitt wegen seines Titels ausblendet, blendet für Händler die ganze
 * Startseite aus, und die Notbremse in filterSections zeigt dann die
 * öffentliche. Genau das ist einmal passiert.
 *
 * Über den Titel und nicht über die ID, weil wir die IDs der angemeldeten
 * Abschnitte nicht alle kennen - und die Regel heilt sich selbst: Wird die
 * Überschrift in der Console geändert, greift sie nicht mehr.
 */
export const DOPPELTE_UEBERSCHRIFTEN = ['willkommen zurück'];

/**
 * Ist diese Überschrift inzwischen doppelt?
 *
 * @param {Object} title das Titelfeld eines Abschnitts ({ content })
 */
export const istDoppelteUeberschrift = title => {
  const roh = title?.content;
  const text = typeof roh === 'string' ? roh.trim().toLowerCase() : '';
  return text !== '' && DOPPELTE_UEBERSCHRIFTEN.includes(text);
};

export const filterSections = (sections, audience) => {
  const list = Array.isArray(sections) ? sections : [];
  const filtered = list.filter(section => {
    const id = section?.sectionId || '';
    if (HIDDEN_SECTION_IDS.includes(id)) {
      return false;
    }
    const rule = SECTION_PREFIXES.find(r => id.startsWith(r.prefix));
    return rule ? rule.audiences.includes(audience) : true;
  });

  // Sicherung: Eine leere Startseite darf nie entstehen. Wenn fuer diese
  // Zielgruppe (noch) kein Abschnitt gepflegt ist - etwa weil eine Anchor-ID
  // fehlt - zeigen wir die oeffentlichen Abschnitte statt einer weissen Seite.
  const hasContent = filtered.some(s => (s?.sectionId || '') !== '' || s?.sectionType);
  if (filtered.length === 0 || !hasContent) {
    return list.filter(section => {
      const id = section?.sectionId || '';
      if (HIDDEN_SECTION_IDS.includes(id)) {
        return false;
      }
      const rule = SECTION_PREFIXES.find(r => id.startsWith(r.prefix));
      return !rule || rule.audiences.includes(ANONYMOUS);
    });
  }

  return filtered;
};

/**
 * Filtert eine Markdown-Linkliste (Footer aus der Console) auf das, was diese
 * Zielgruppe sehen soll. Der Footer ist ein Textblock, kein Linkobjekt –
 * deshalb arbeiten wir zeilenweise und lassen alles stehen, was wir nicht
 * eindeutig als eingeschraenkten Link erkennen.
 *
 * @param {string} markdown Text des Footer-Blocks
 * @param {string} audience Rueckgabewert von getAudience
 * @returns {string} gefilterter Text
 */
export const filterFooterMarkdown = (markdown, audience) => {
  if (typeof markdown !== 'string' || markdown === '') return markdown;

  return markdown
    .split('\n')
    .filter(line => {
      const match = line.match(/\]\(([^)\s]+)/);
      const href = match ? match[1] : null;
      const allowed = href ? NAV_AUDIENCES[href] : null;
      return allowed ? allowed.includes(audience) : true;
    })
    .join('\n');
};

/**
 * Welche Inhaltsseite (/p/<id>) sieht wer?
 *
 * Die Links haben wir schon gefiltert, aber ein Lesezeichen, ein alter Tab
 * oder eine Suchmaschine fuehren trotzdem hin. Deshalb entscheidet zusaetzlich
 * die Seite selbst: Ein Haendler hat auf der Seite fuer Golfanlagen nichts zu
 * suchen und umgekehrt. Nicht aufgefuehrte Seiten sind fuer alle offen.
 */
export const PAGE_AUDIENCES = {
  'fuer-golfanlagen': [ANONYMOUS, CLUB],
  'fuer-haendler': [ANONYMOUS, DEALER],
};

export const mayViewPage = (pageId, audience) => {
  const allowed = PAGE_AUDIENCES[pageId];
  return allowed ? allowed.includes(audience) : true;
};
